/**
 * Offline Sync Queue — persistent FIFO backed by IndexedDB.
 *
 * Feb 2026. Used by the Warehouse Floor app so `Scan` and `Complete
 * Task` POSTs don't get lost when the device drops off Wi-Fi in the
 * cold-storage aisle. Behaviour:
 *
 *   • `enqueue(request)`  — persist a request { url, method, body,
 *                            headers, kind, ts, retries, conflict }.
 *   • `list()`            — return all pending items ordered by ts.
 *   • `remove(id)`        — drop an item once the server 2xx-ed it.
 *   • `markConflict(...)` — flag a 409/duplicate/gone task so the
 *                           drain loop stops retrying it forever.
 *   • `subscribe(cb)`     — pub/sub so the React status badge can
 *                            re-render whenever the queue mutates.
 *
 * All ops are async. IndexedDB is opened lazily on first call and
 * cached for the tab lifetime. Every mutation notifies subscribers.
 *
 * Non-goals: cross-tab dedupe (each tab owns its own drain loop but
 * the DB is shared — the drain checks `remove()` atomicity via
 * transactions, so a double-drain across tabs is safe if wasteful).
 */

const DB_NAME = 'wh-floor-sync';
const DB_VERSION = 1;
const STORE = 'requests';

const subscribers = new Set();

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          const store = db.createObjectStore(STORE, { keyPath: 'id' });
          store.createIndex('ts', 'ts', { unique: false });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (err) {
      reject(err);
    }
  });
  return _dbPromise;
}

function txn(mode = 'readonly') {
  return openDb().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

function notify() {
  list().then((rows) => {
    subscribers.forEach((cb) => {
      try { cb(rows); } catch (err) { /* subscriber error is not fatal */ }
    });
  });
}

function makeId() {
  // Short-and-sweet ID — timestamped so FIFO ordering is trivial even
  // if the client's clock skews. Sortable as strings.
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export async function enqueue(request) {
  const item = {
    id: makeId(),
    ts: Date.now(),
    retries: 0,
    conflict: null,
    ...request,
  };
  const store = await txn('readwrite');
  await new Promise((resolve, reject) => {
    const r = store.add(item);
    r.onsuccess = resolve;
    r.onerror = () => reject(r.error);
  });
  notify();
  return item;
}

export async function list() {
  const store = await txn('readonly');
  return new Promise((resolve, reject) => {
    const r = store.getAll();
    r.onsuccess = () => {
      const rows = (r.result || []).slice().sort((a, b) => a.ts - b.ts);
      resolve(rows);
    };
    r.onerror = () => reject(r.error);
  });
}

export async function remove(id) {
  const store = await txn('readwrite');
  await new Promise((resolve, reject) => {
    const r = store.delete(id);
    r.onsuccess = resolve;
    r.onerror = () => reject(r.error);
  });
  notify();
}

export async function markConflict(id, reason) {
  const store = await txn('readwrite');
  await new Promise((resolve, reject) => {
    const g = store.get(id);
    g.onsuccess = () => {
      const rec = g.result;
      if (!rec) return resolve();
      rec.conflict = String(reason || 'conflict');
      const p = store.put(rec);
      p.onsuccess = resolve;
      p.onerror = () => reject(p.error);
    };
    g.onerror = () => reject(g.error);
  });
  notify();
}

export async function bumpRetry(id) {
  const store = await txn('readwrite');
  await new Promise((resolve, reject) => {
    const g = store.get(id);
    g.onsuccess = () => {
      const rec = g.result;
      if (!rec) return resolve();
      rec.retries = (rec.retries || 0) + 1;
      const p = store.put(rec);
      p.onsuccess = resolve;
      p.onerror = () => reject(p.error);
    };
    g.onerror = () => reject(g.error);
  });
  notify();
}

export function subscribe(cb) {
  subscribers.add(cb);
  // Prime the subscriber with the current state so the badge doesn't
  // flash "loading" on first mount.
  list().then((rows) => { try { cb(rows); } catch (e) { /* noop */ } });
  return () => subscribers.delete(cb);
}
